import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type { MusicContextType } from '@tunasong/music-lib'

// State for the editors
export interface EditorState {
  [id: string]:
    | {
        context: MusicContextType
      }
    | undefined
  default: {
    context: MusicContextType
  }
}

// Define the initial state using that type
const initialState: EditorState = {
  default: {
    context: {},
  },
}

export const editorsSlice = createSlice({
  name: 'editors',
  initialState,
  reducers: {
    setDefaultContext: (state, action: PayloadAction<{ context: Partial<MusicContextType> }>) => {
      Object.assign(state.default.context, action.payload.context)
    },
    setContext: (state, action: PayloadAction<{ id: string; context: Partial<MusicContextType> }>) => {
      const { id, context } = action.payload
      const c = (state[id] = state[id] ?? { context: {} })
      Object.assign(c.context, context)
    },
    /**
     * Drop an editor's context entry. Per-song editors get a fresh random
     * `editor.id` and store chord/section context that references document
     * subtrees; without eviction on unmount, every song switch retained a
     * document tree in this slice forever → renderer OOM (GH #1628). The shared
     * `default` context is never removed — it is not tied to any one editor.
     */
    clearContext: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload
      if (id === 'default') {
        return
      }
      delete state[id]
    },
  },
})
